import { selectedValuesFilters } from './../../../../../util/interfaces/myAssetInterface';
import { Component, computed, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
	faChartSimple,
	faCircleMinus,
	faCirclePlus,
	faExternalLink,
	faFolderOpen,
	faLink,
	faTimes,
	faArrowUp,
	faArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { cockpitChartInterface } from 'src/app/util/interfaces/cockpitRework/cockpitRework';
import { LabelsService } from 'src/app/util/services/labels/labels.service';
import { CockpitChartService } from './service/cockpit-chart.service';
import { IndicatorsService } from '../filter-header/service/filter-header.service';
import { StatusCiTypeService } from 'src/app/page/register-page/register-forms/cmdb/status-ci-type/service/status-ci-type.service';
import { StatusInterfaceCMDB } from 'src/app/util/interfaces/statusInterface';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { MyAssetService } from 'src/app/page/my-asset/data/my-asset.service';
import { convertTo2DArray } from 'src/app/util/function/converter';
import { ExcelService } from 'src/app/services/excel/excel.service';
import { LoadingNotificationService } from 'src/app/services/loading-notification/loading-notification.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { GlobalService } from 'src/app/services/global/global.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import { NgChartsModule } from 'ng2-charts';

interface CiType {
	ciType: number;
	subType: number | string;
	type: string;
}

interface Location {
	id_location: string;
	site: string;
	location: string;
}

interface Manufacturer {
	id: string;
	manufacturer: string;
}

interface Model {
	id: string;
	model: string;
	manufacturer: string;
	name_manufacturer?: string;
}

interface SubType {
	id: string | number;
	name: string;
	label: string;
	ci_type: string;
}

@Component({
	standalone: true,
	imports: [CommonModule, FontAwesomeModule, NgChartsModule],
	selector: 'pcp-cockpit-chart',
	templateUrl: './cockpit-chart.component.html',
	styleUrls: ['./cockpit-chart.component.scss'],
})
export class CockpitChartComponent implements OnInit, OnDestroy {
	@Input()
	labels: LabelsInterface = {
		lbl_ckp_sem_indicadores_va_gerenciamento: '',
		lbl_selecione_valor_indicador_visualizar_relatorio: '',
		lbl_indicador_sem_dado_periodo_selecionado: '',
		lbl_geral_deletado_com_sucesso: '',
	};

	@Input()
	showChartValues = true;

	icons = {
		faTimes: faTimes,
		faExternalLink: faExternalLink,
		faChartSimple: faChartSimple,
		faCirclePlus: faCirclePlus,
		faCircleMinus: faCircleMinus,
		faLink: faLink,
		faFolderOpen: faFolderOpen,
		faArrowUp: faArrowUp,
		faArrowDown: faArrowDown,
	};

	constructor(
		private LabelsService: LabelsService,
		private CockpitChartService: CockpitChartService,
		private NotificationService: NotificationService,
		private router: Router,
		private indicatorsService: IndicatorsService,
		private statusCiTypeService: StatusCiTypeService,
		private MyAssetService: MyAssetService,
		private ExcelService: ExcelService,
		private LoadingNotificationService: LoadingNotificationService,
		private ProfileService: ProfileService,
		public globalService: GlobalService
	) {
		// Registrar o plugin ChartDataLabels
		Chart.register(...registerables, ChartDataLabels);
		this.labels = computed(() => this.LabelsService.labels())();
	}

	indexChartHover = -1;
	darkMode = false;

	chartConfigs: { [chartId: string]: { size: number; indexAxis?: string } } = {};

	barChartSize = 2;
	barChartOptions: ChartConfiguration['options'] = {
		responsive: true,
		scales: {
			y: {
				min: 0,
				ticks: {
					precision: 0,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
				stacked: true, // para colocar tudo na mesma coluna
			},
			x: {
				ticks: {
					precision: 0,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
				stacked: true,
			},
		},
		indexAxis: 'y',
		plugins: {
			datalabels: {
				anchor: (context) => {
					const value = context.dataset.data[context.dataIndex] as number;
					return value > 20 ? 'center' : 'end';
				},
				align: (context) => {
					const value = context.dataset.data[context.dataIndex] as number;
					return value > 20 ? 'center' : 'end';
				},
				display: (context) => {
					if (!this.showChartValues) {
						return false;
					}
					const dataset = context.chart.data.datasets[context.datasetIndex];
					const value = context.dataset.data[context.dataIndex] as number;
					return !dataset.hidden && value > 0;
				},
				formatter: (value, context) => {
					return Math.round(value);
				},
				color: (context) => {
					const value = context.dataset.data[context.dataIndex] as number;
					// Se valor > 20, usa branco; senão usa a cor da barra
					if (value > 20) {
						return '#fff';
					}
					const backgroundColor = context.dataset.backgroundColor as any;
					if (Array.isArray(backgroundColor)) {
						const colorValue = backgroundColor[context.dataIndex];
						return Array.isArray(colorValue) ? colorValue[0] : colorValue;
					}
					return backgroundColor || '#fff';
				},
				font: {
					weight: 'bold',
					size: 14,
				},
				backgroundColor: 'transparent',
				borderRadius: 4,
				padding: 4,
			},
		},
	};

	pieChartSize = 1;
	pieChartOptions: ChartConfiguration['options'] | any = {
		responsive: true,
		plugins: {
			legend: {
				display: true,
				position: 'right',
				labels: {
					fontSize: 12,
					usePointStyle: true,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
			},
			datalabels: {
				display: false,
			},
		},
	};

	gaugeChartSize = 1;
	gaugeChartOptions: ChartConfiguration['options'] | any = {
		responsive: true,
		circumference: 180,
		rotation: -90,
		plugins: {
			legend: {
				display: true,
				position: 'top',
				labels: {
					fontSize: 12,
					usePointStyle: true,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
			},
			datalabels: {
				display: false,
			},
		},
	};

	lineChartSize = 2;
	lineChartOptions: ChartConfiguration['options'] = {
		responsive: true,
		scales: {
			y: {
				min: 0,
				ticks: {
					precision: 0,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
			},
			x: {
				ticks: {
					precision: 0,
					color: this.darkMode ? '#ffffff' : '#333333',
				},
			},
		},
		plugins: {
			datalabels: {
				anchor: 'end',
				align: 'end',
				display: (context) => {
					if (!this.showChartValues) {
						return false;
					}
					const value = context.dataset.data[context.dataIndex] as number;
					return value > 0;
				},
				formatter: (value) => {
					return Math.round(value);
				},
				color: '#fff',
				font: {
					weight: 'bold',
					size: 12,
				},
				backgroundColor: 'transparent',
				borderRadius: 4,
				padding: 4,
			},
		},
	};

	availableCiTypes: CiType[] = [];
	availableLocations: Location[] = [];
	availableStatus: StatusInterfaceCMDB[] = [];
	availableManufacturers: Manufacturer[] = [];
	availableModels: Model[] = [];
	availableSubTypes: SubType[] = [];

	@Input()
	cockpitCards: cockpitChartInterface[] = [];

	@Input()
	cockpitCharts: cockpitChartInterface[] = [];

	@Input() config: { hideDelete: boolean; hideResize: boolean; defaultChartSize: number } = {
		hideDelete: false,
		hideResize: false,
		defaultChartSize: undefined,
	};

	@Input() selectedValuesFilters: selectedValuesFilters;

	@Input() isLoadingCiIndicators = false;

	ngOnInit(): void {
		// Carrega as configurações individuais dos charts
		this.loadChartConfigs();

		// Carrega as opções disponíveis
		this.loadAvailableCiTypes();
		this.loadAvailableLocations();
		this.loadAvailableStatus();
		this.loadAvailableManufacturers();
		this.loadAvailableModels();

		this.globalService.darkModeOn.subscribe((isDarkMode) => {
			this.darkMode = isDarkMode;
			this.updateChartColors();
		});
	}

	ngOnDestroy(): void {
		this.saveChartConfigs();
	}

	loadChartConfigs(): void {
		const saved = localStorage.getItem('cockpitIndividualConfigs');
		if (saved) {
			try {
				this.chartConfigs = JSON.parse(saved);
			} catch (error) {
				console.error('Erro ao carregar configurações dos charts:', error);
				this.chartConfigs = {};
			}
		}
	}

	saveChartConfigs(): void {
		localStorage.setItem('cockpitIndividualConfigs', JSON.stringify(this.chartConfigs));
	}

	updateChartColors(): void {
		// barChart
		if (this.barChartOptions?.scales) {
			if (this.barChartOptions.scales.y?.ticks) {
				this.barChartOptions.scales.y.ticks.color = this.darkMode ? '#ffffff' : '#333333';
			}
			if (this.barChartOptions.scales.x?.ticks) {
				this.barChartOptions.scales.x.ticks.color = this.darkMode ? '#ffffff' : '#333333';
			}
		}

		// pieChart
		if (this.pieChartOptions?.plugins?.legend?.labels) {
			this.pieChartOptions.plugins.legend.labels.color = this.darkMode ? '#ffffff' : '#333333';
		}

		// gaugeChart
		if (this.gaugeChartOptions?.plugins?.legend?.labels) {
			this.gaugeChartOptions.plugins.legend.labels.color = this.darkMode ? '#ffffff' : '#333333';
		}

		// lineChart
		if (this.lineChartOptions?.scales) {
			if (this.lineChartOptions.scales.y?.ticks) {
				this.lineChartOptions.scales.y.ticks.color = this.darkMode ? '#ffffff' : '#333333';
			}
			if (this.lineChartOptions.scales.x?.ticks) {
				this.lineChartOptions.scales.x.ticks.color = this.darkMode ? '#ffffff' : '#333333';
			}
		}

		this.updateChart();
	}

	getChartSize(chart: cockpitChartInterface): number {
		if (this.config.defaultChartSize !== null && this.config.defaultChartSize !== undefined) {
			return this.config.defaultChartSize;
		}
		if (this.chartConfigs[chart.id]?.size) {
			return this.chartConfigs[chart.id].size;
		}
		// Retorna tamanho padrão baseado no tipo
		return this[chart.type + 'ChartSize'] || 1;
	}

	setChartSize(chart: cockpitChartInterface, size: number): void {
		if (this.chartConfigs[chart.id]) {
			this.chartConfigs[chart.id].size = size;
		} else {
			this.chartConfigs[chart.id] = { size };
		}
		this.saveChartConfigs();
	}

	getChartIndexAxis(chart: cockpitChartInterface): string {
		if (chart.type === 'bar' && this.chartConfigs[chart.id]?.indexAxis) {
			return this.chartConfigs[chart.id].indexAxis;
		}
		return (this.barChartOptions.indexAxis as string) || 'y';
	}

	setChartIndexAxis(chart: cockpitChartInterface, indexAxis: string): void {
		if (this.chartConfigs[chart.id]) {
			this.chartConfigs[chart.id].indexAxis = indexAxis;
		} else {
			this.chartConfigs[chart.id] = { size: this.getChartSize(chart), indexAxis };
		}
		this.saveChartConfigs();
	}

	getChartOptions(chart: cockpitChartInterface): any {
		if (chart.type === 'bar') {
			return {
				...this.barChartOptions,
				indexAxis: this.getChartIndexAxis(chart),
				plugins: {
					...this.barChartOptions.plugins,
					datalabels: {
						anchor: (context) => {
							const value = context.dataset.data[context.dataIndex] as number;
							return value > 20 ? 'center' : 'end';
						},
						align: (context) => {
							const value = context.dataset.data[context.dataIndex] as number;
							return value > 20 ? 'center' : 'end';
						},
						display: (context) => {
							if (!this.showChartValues) {
								return false;
							}
							const dataset = context.chart.data.datasets[context.datasetIndex];
							const value = context.dataset.data[context.dataIndex] as number;
							return !dataset.hidden && value > 0;
						},
						formatter: (value, context) => {
							return Math.round(value);
						},
						color: (context) => {
							const value = context.dataset.data[context.dataIndex] as number;
							// Se valor > 20, usa branco; senão usa a cor da barra
							if (value > 20) {
								return '#fff';
							}
							const backgroundColor = context.dataset.backgroundColor as any;
							if (Array.isArray(backgroundColor)) {
								const colorValue = backgroundColor[context.dataIndex];
								return Array.isArray(colorValue) ? colorValue[0] : colorValue;
							}
							return backgroundColor || '#fff';
						},
						font: {
							weight: 'bold',
							size: 14,
						},
						backgroundColor: 'transparent',
						borderRadius: 4,
						padding: 4,
					},
				},
			};
		}
		if (chart.type === 'line') {
			return {
				...this.lineChartOptions,
				plugins: {
					...this.lineChartOptions.plugins,
					datalabels: {
						anchor: 'end',
						align: 'end',
						display: (context) => {
							if (!this.showChartValues) {
								return false;
							}
							const value = context.dataset.data[context.dataIndex] as number;
							return value > 0;
						},
						formatter: (value, context) => {
							return Math.round(value);
						},
						color: '#fff',
						font: {
							weight: 'bold',
							size: 12,
						},
						backgroundColor: 'transparent',
						borderRadius: 4,
						padding: 4,
					},
				},
			};
		}
		return this[chart.type + 'ChartOptions'];
	}

	updateChart(): void {
		// Salva as configurações quando o chart é atualizado
		this.saveChartConfigs();

		const temporary = this.cockpitCharts;
		this.cockpitCharts = [];
		setTimeout(() => {
			this.cockpitCharts = temporary;
		}, 1);
	}

	deleteChart(chart: cockpitChartInterface, index: number): void {
		let indicatorType = '';
		if (chart.custom === false) {
			if (chart.type === 'text') {
				this.cockpitCards.splice(index, 1);
			} else {
				this.cockpitCharts.splice(index, 1);
			}
			if (localStorage.getItem('ckpO')) {
				const parseCkpo = JSON.parse(localStorage.getItem('ckpO'));
				for (const [index, ckpId] of parseCkpo.entries()) {
					if (ckpId === chart.id) {
						parseCkpo.splice(index, 1);
						localStorage.setItem('ckpO', JSON.stringify(parseCkpo));
					}
				}
			}
		} else if (chart.custom === true) {
			indicatorType = 'custom';
			if (chart.type === 'text') {
				this.cockpitCards.splice(index, 1);
			} else {
				this.cockpitCharts.splice(index, 1);
			}
			if (localStorage.getItem('ckpC')) {
				const parseCkpC = JSON.parse(localStorage.getItem('ckpC'));
				for (const [index, ckpId] of parseCkpC.entries()) {
					if (ckpId === chart.id) {
						parseCkpC.splice(index, 1);
						localStorage.setItem('ckpC', JSON.stringify(parseCkpC));
					}
				}
			}
		}

		// Remove do cache cockpitIndividualConfigs
		if (localStorage.getItem('cockpitIndividualConfigs')) {
			try {
				const configs = JSON.parse(localStorage.getItem('cockpitIndividualConfigs'));
				if (configs[chart.id]) {
					delete configs[chart.id];
					localStorage.setItem('cockpitIndividualConfigs', JSON.stringify(configs));
				}
			} catch (error) {
				console.error('Erro ao remover do cockpitIndividualConfigs:', error);
			}
		}

		this.CockpitChartService.deleteChartById(chart.id, indicatorType).subscribe(() => {});

		this.NotificationService.setNotificationMessage(this.labels.lbl_geral_deletado_com_sucesso);
	}

	goToChartReport(chart: cockpitChartInterface): void {
		if (!chart.availableReports) {
			this.NotificationService.setNotificationMessage('Relatório não disponível para este gráfico.');
			return;
		}

		if (chart.chartData.datasets.length === 0) {
			this.NotificationService.setNotificationMessage('Não há dados disponíveis para exportar no período selecionado.');
			return;
		}

		const idNotificaton = this.LoadingNotificationService.setNotificationMessage(
			`${this.labels.lbl_geral_my_asset} - ${this.labels.lbl_geral_movimentacoes}`,
			'loading',
			'faFileAlt'
		);

		// Apenas flow_cmdb tem API implementada por enquanto, que é o mesmo relatório de movimentações do My Asset,
		// então aproveitamos para usar esse endpoint já existente. Outros tipos de relatório serão implementados futuramente.
		// Será necessário passar por cada indicador do cockpit e gerar o relatório, habilitando o availableReports para true
		if (chart.title === 'flow_cmdb') {
			setTimeout(() => {
				this.MyAssetService.exportMovements(this.selectedValuesFilters).subscribe(
					(resp) => {
						if (!resp || (Array.isArray(resp) && resp.length === 0)) {
							this.NotificationService.setNotificationMessage('Não há movimentações para os filtros selecionados');
							this.LoadingNotificationService.updateNotificationStatus(idNotificaton, 'attention');
							return;
						}
						this.LoadingNotificationService.updateNotificationStatus(idNotificaton, 'success');
						const array2d = convertTo2DArray(resp);
						return this.ExcelService.exportAsExcelFile(
							array2d,
							`${this.labels.lbl_geral_relatorio} - ${this.labels.lbl_geral_movimentacoes}`
						);
					},
					(error) => {
						console.error('Ocorreu um erro ao exportar os dados:', error);
						this.LoadingNotificationService.updateNotificationStatus(idNotificaton, 'error');
					}
				);
			}, 5);
		} else {
			this.LoadingNotificationService.updateNotificationStatus(idNotificaton, 'error');
			this.NotificationService.setNotificationMessage('Exportação ainda não disponível para este tipo de relatório.');
		}
	}

	// Função auxiliar para normalizar strings (case-insensitive e trim)
	private normalizeString(string_: string): string {
		return string_?.toString().toLowerCase().trim() || '';
	}

	// Função auxiliar para busca case-insensitive em listas
	private findInList<T>(list: T[], searchValue: string, matchFields: ((item: T) => string | undefined)[]): T | undefined {
		const normalizedSearch = this.normalizeString(searchValue);
		return list.find((item) =>
			matchFields.some((field) => {
				const fieldValue = field(item);
				return fieldValue && this.normalizeString(fieldValue) === normalizedSearch;
			})
		);
	}

	onBarChartClick(event: { event?: any; active?: any[] }, chartIndex: number): void {
		const chart = this.cockpitCharts[chartIndex];

		// Só permite clique se availableDrillDown for true
		if (!chart.availableDrillDown) {
			this.NotificationService.setNotificationMessage('Drill down não disponível para este gráfico.');
			return;
		}

		if (event.active && event.active.length > 0) {
			const activeElement = event.active[0];
			const label = (chart.chartData.labels?.[activeElement.index] as string) || '';
			const family = chart.family || 'cmdb';
			const queryParameters: any = {};

			// Verifica se o label contém " - " (valores concatenados)
			let labelParts: string[] = [];
			labelParts = label.includes(' - ') ? label.split(' - ').map((part) => part.trim()) : [label.trim()];

			// PRIORIDADE 1: Busca no drillDownIndex do chart (configuração específica)
			if (chart.drillDownIndex) {
				for (const parameterKey of Object.keys(chart.drillDownIndex)) {
					console.log(chart);
					const indexMapping = chart.drillDownIndex[parameterKey];
					if (indexMapping && typeof indexMapping === 'object') {
						// Procura TODOS os IDs correspondentes ao label completo (case-insensitive)
						// Ex: "0" e "" podem ter o mesmo label "Termo não gerado"
						const foundIds = Object.keys(indexMapping).filter(
							(id) => this.normalizeString(indexMapping[id]) === this.normalizeString(label)
						);

						if (foundIds.length > 0) {
							const normalizedIds = foundIds.map((id) => (id === '' ? 'NULL' : id));
							queryParameters[parameterKey] = normalizedIds.join(',');
						}

						if (foundIds.length === 0 && labelParts.length > 1) {
							const partIds: string[] = [];
							for (const part of labelParts) {
								if (!part || part === '') {
									if (!queryParameters[parameterKey]) {
										partIds.push('NULL');
									}
									continue;
								}

								const matchingIds = Object.keys(indexMapping).filter(
									(id) => this.normalizeString(indexMapping[id]) === this.normalizeString(part)
								);
								partIds.push(...matchingIds.map((id) => (id === '' ? 'NULL' : id)));
							}

							if (partIds.length > 0 && !queryParameters[parameterKey]) {
								queryParameters[parameterKey] = partIds.join(',');
							}
						}
					}
				}
			}

			if (Object.keys(queryParameters).length === 0) {
				const exactStatusMatch = this.findInList(this.availableStatus, label, [(item) => item.status]);
				if (exactStatusMatch) {
					queryParameters.status = exactStatusMatch.status;
				}

				const exactCiTypeMatch = this.findInList(this.availableCiTypes, label, [
					(item) => item.type,
					(item) => this.labels[item.type],
				]);
				if (exactCiTypeMatch) {
					queryParameters.type = exactCiTypeMatch.ciType;
					if (exactCiTypeMatch.subType) {
						queryParameters.sub_type = exactCiTypeMatch.subType;
					}
				}

				const fullLocation = this.availableLocations.find(
					(item) => this.normalizeString(`${item.site} - ${item.location}`) === this.normalizeString(label)
				);
				if (fullLocation && !queryParameters.location) {
					queryParameters.location = fullLocation.id_location;
				}

				// Se não encontrou nenhum match exato com o label completo, processa as partes
				if (!queryParameters.type && !queryParameters.status && !queryParameters.location) {
					if (labelParts.length === 2) {
						const [firstPart, secondPart] = labelParts;
						const sortedParts = [...labelParts].sort((a, b) => b.length - a.length);

						const ciTypeItem = this.findInList(this.availableCiTypes, firstPart, [
							(item) => item.type,
							(item) => this.labels[item.type],
						]);
						if (ciTypeItem) {
							queryParameters.type = ciTypeItem.ciType;
						}

						const subTypeItem = this.availableCiTypes.find((item) => {
							if (!item.subType || item.subType === '') {
								return false;
							}
							const itemLabel = this.labels[item.type] || item.type;
							return (
								this.normalizeString(itemLabel) === this.normalizeString(secondPart) ||
								this.normalizeString(item.type) === this.normalizeString(secondPart)
							);
						});

						if (subTypeItem) {
							queryParameters.sub_type = subTypeItem.subType;
							if (!queryParameters.type) {
								queryParameters.type = subTypeItem.ciType;
							}
						} else {
							const subType = this.findInList(this.availableSubTypes, secondPart, [
								(item) => item.name,
								(item) => item.label,
							]);
							if (subType) {
								queryParameters.sub_type = subType.id;
								if (!queryParameters.type && subType.ci_type) {
									queryParameters.type = subType.ci_type;
								}
							}
						}

						for (const part of sortedParts) {
							const status = this.findInList(this.availableStatus, part, [(item) => item.status]);
							if (status && !queryParameters.status) {
								queryParameters.status = status.status;
							}

							const location = this.findInList(this.availableLocations, part, [(item) => item.location, (item) => item.site]);
							if (location && !queryParameters.location) {
								queryParameters.location = location.id_location;
							}

							const manufacturer = this.findInList(this.availableManufacturers, part, [(item) => item.manufacturer]);
							if (manufacturer && !queryParameters.manufacturer) {
								queryParameters.manufacturer = manufacturer.id;
							}

							const model = this.findInList(this.availableModels, part, [(item) => item.model]);
							if (model) {
								const currentModelLength = queryParameters.model
									? this.availableModels.find((m) => m.id == queryParameters.model)?.model.length || 0
									: 0;
								if (model.model.length >= currentModelLength) {
									queryParameters.model = model.id;
								}
							}
						}
					} else {
						// Fallback: processa cada parte individualmente
						// Ordena as partes por tamanho (mais longas primeiro) para priorizar matches mais específicos
						const sortedParts = [...labelParts].sort((a, b) => b.length - a.length);

						for (const part of sortedParts) {
							if (!part || part === '') {
								if (!queryParameters.manufacturer) queryParameters.manufacturer = 'NULL';
								if (!queryParameters.model) queryParameters.model = 'NULL';
								continue;
							}

							const ciType = this.findInList(this.availableCiTypes, part, [
								(item) => item.type,
								(item) => this.labels[item.type],
							]);
							if (ciType && !queryParameters.type) {
								queryParameters.type = ciType.ciType;
								if (ciType.subType && ciType.subType !== '') {
									queryParameters.sub_type = ciType.subType;
								}
							}

							// Verifica em availableSubTypes (busca pelo nome do subtype) - case-insensitive
							// Agora permite sobrescrever se encontrar um match melhor
							const subType = this.findInList(this.availableSubTypes, part, [(item) => item.name, (item) => item.label]);
							if (subType) {
								queryParameters.sub_type = subType.id;
								if (!queryParameters.type && subType.ci_type) {
									queryParameters.type = subType.ci_type;
								}
							}

							const status = this.findInList(this.availableStatus, part, [(item) => item.status]);
							if (status && !queryParameters.status) {
								queryParameters.status = status.status;
							}

							const location = this.findInList(this.availableLocations, part, [(item) => item.location, (item) => item.site]);
							if (location && !queryParameters.location) {
								queryParameters.location = location.id_location;
							}

							const manufacturer = this.findInList(this.availableManufacturers, part, [(item) => item.manufacturer]);
							if (manufacturer && !queryParameters.manufacturer) {
								queryParameters.manufacturer = manufacturer.id;
							}

							const model = this.findInList(this.availableModels, part, [(item) => item.model]);
							if (model) {
								const currentModelLength = queryParameters.model
									? this.availableModels.find((m) => m.id == queryParameters.model)?.model.length || 0
									: 0;
								if (model.model.length >= currentModelLength) {
									queryParameters.model = model.id;
								}
							}
						}
					}
				}
			}

			if (Object.keys(queryParameters).length > 0) {
				const parameters = new URLSearchParams();
				for (const key of Object.keys(queryParameters)) {
					parameters.append(key, queryParameters[key]);
				}

				console.log(this.selectedValuesFilters);
				//Concatenar os parâemtros já definidos pelo drilldrown com os parâmetros selecionados no filtro
				const appendOrJoinParameter = (key: string, value: string[]): void => {
					for (const v of value) {
						const currentValue = parameters.get(key);
						console.log(v, currentValue, currentValue?.split(','));
						if (currentValue && currentValue.split(',').includes(v)) continue;
						parameters.set(key, currentValue ? `${currentValue},${v}` : v);
					}
				};

				if (this.selectedValuesFilters?.ciType) appendOrJoinParameter('type', this.selectedValuesFilters.ciType.split(','));
				if (this.selectedValuesFilters?.subType) appendOrJoinParameter('sub_type', this.selectedValuesFilters.subType.split(','));
				if (this.selectedValuesFilters?.location) appendOrJoinParameter('location', this.selectedValuesFilters.location.split(','));
				if (this.selectedValuesFilters?.statuses?.length > 0) appendOrJoinParameter('status', this.selectedValuesFilters.statuses);

				// Ajuste específico para family 'cmdb' que deve redirecionar para 'cmdb/list'
				let familyUrl = family;
				if (family === 'cmdb') {
					familyUrl = 'cmdb/list';
				}
				const url = `/${familyUrl}?${parameters.toString()}`;
				window.open(url, '_blank');
			}
		}
	}

	chartSelect(event: any): void {
		if (event.active && event.active.length > 0 && this.cockpitCharts[this.indexChartHover].availableDrillDown) {
			const idIndicator = this.cockpitCharts[this.indexChartHover].id;
			let genericParameter1 = this.cockpitCharts[this.indexChartHover].chartData.labels[event.active[0].index];

			if (this.cockpitCharts[this.indexChartHover].multiple && this.cockpitCharts[this.indexChartHover].multiple === true) {
				genericParameter1 = `${genericParameter1}§${this.cockpitCharts[this.indexChartHover].chartData.datasets[event.active[0].datasetIndex].label}`;
			}

			this.goToChartReport(this.cockpitCharts[this.indexChartHover]);
		}
	}

	onDropCard(event: CdkDragDrop<cockpitChartInterface[]>): void {
		if (event.previousIndex !== event.currentIndex) {
			moveItemInArray(this.cockpitCards, event.previousIndex, event.currentIndex);
		}
	}

	onDropChart(event: CdkDragDrop<cockpitChartInterface[]>): void {
		if (event.previousIndex !== event.currentIndex) {
			moveItemInArray(this.cockpitCharts, event.previousIndex, event.currentIndex);
			this.saveChartOrder();
		}
	}

	saveChartOrder(): void {
		const isCustom = this.cockpitCharts.length > 0 && this.cockpitCharts[0].custom === true;
		const storageKey = isCustom ? 'ckpC' : 'ckpO';
		const chartOrder = this.cockpitCharts.map((chart) => chart.id);
		localStorage.setItem(storageKey, JSON.stringify(chartOrder));
	}

	moveChartUp(index: number): void {
		if (index > 0) {
			moveItemInArray(this.cockpitCharts, index, index - 1);
			this.saveChartOrder();
		}
	}

	moveChartDown(index: number): void {
		if (index < this.cockpitCharts.length - 1) {
			moveItemInArray(this.cockpitCharts, index, index + 1);
			this.saveChartOrder();
		}
	}

	moveCardUp(index: number): void {
		if (index > 0) {
			moveItemInArray(this.cockpitCards, index, index - 1);
		}
	}

	moveCardDown(index: number): void {
		if (index < this.cockpitCards.length - 1) {
			moveItemInArray(this.cockpitCards, index, index + 1);
		}
	}

	loadAvailableCiTypes(): void {
		this.indicatorsService.getAvailablesCiTypes().subscribe({
			next: (response) => {
				this.availableCiTypes = response.map((item: any) => ({
					ciType: item.ciType,
					subType: item.subType || '',
					type: item.type || '',
				}));

				this.loadAvailableSubTypes();
			},
			error: (error) => console.error('Erro ao carregar tipos de CI:', error),
		});
	}

	loadAvailableLocations(): void {
		this.indicatorsService.getAvailablesLocations().subscribe({
			next: (response) => {
				this.availableLocations = response;
			},
			error: (error) => console.error('Erro ao carregar localizações:', error),
		});
	}

	loadAvailableStatus(): void {
		this.statusCiTypeService.getStatusCiType().subscribe({
			next: (response) => {
				this.availableStatus = response;
			},
			error: (error) => console.error('Erro ao carregar status:', error),
		});
	}

	loadAvailableManufacturers(): void {
		this.indicatorsService.getAvailablesManufacturers().subscribe({
			next: (response) => {
				this.availableManufacturers = response.map((item: any) => ({
					id: item.id,
					manufacturer: item.manufacturer,
				}));
			},
			error: (error) => console.error('Erro ao carregar fabricantes:', error),
		});
	}

	loadAvailableModels(): void {
		this.indicatorsService.getAvailablesModels().subscribe({
			next: (response) => {
				this.availableModels = response.map((item: any) => ({
					id: item.id,
					model: item.model,
					manufacturer: item.manufacturer,
					name_manufacturer: item.name_manufacturer,
				}));
			},
			error: (error) => console.error('Erro ao carregar modelos:', error),
		});
	}

	loadAvailableSubTypes(): void {
		this.indicatorsService.getAvailablesSubTypes().subscribe({
			next: (response: any) => {
				const subtypesArray = Array.isArray(response) ? response : response?.data || response?.items || [];

				this.availableSubTypes = subtypesArray.map((item: any) => ({
					id: item.id,
					name: item.name,
					label: item.label,
					ci_type: item.ci_type,
				}));
			},
			error: (error) => console.error('Erro ao carregar subtipos:', error),
		});
	}
}
